import React, { useEffect, useRef, useState, useMemo } from "react";
import { twJoin } from "tailwind-merge";

import SupDepositAlt from "../../../icons/DepositAlt";
import SupWithdrawAlt from "../../../icons/WithdrawAlt";
import { toPixels } from "../../../utils/css";

import { type BaseButtonInstance, BaseButton } from "./components/BaseButton";
import { type RemoveButtonInstance, RemoveButton } from "./components/RemoveButton";

export interface DepositWithdrawButtonProps {
  fluid?: boolean;
  completed?: boolean | [boolean, boolean];
  disabled?: boolean | [boolean, boolean];
  defaultButton?: "primary" | "secondary";
  onDeposit?: () => void;
  onWithdraw?: () => void;
  onRemoveDeposit?: () => void;
  onRemoveWithdraw?: () => void;
}

const CONTAINER_PADDING_RIGHT = 40;

export function SupDepositWithdrawButton({
  fluid = false,
  defaultButton = "primary",
  disabled = false,
  completed = false,
  onDeposit,
  onWithdraw,
  onRemoveDeposit,
  onRemoveWithdraw,
}: DepositWithdrawButtonProps) {
  const depositButtonRef = useRef<BaseButtonInstance>(null);
  const withdrawButtonRef = useRef<BaseButtonInstance>(null);
  const removeButtonRef = useRef<RemoveButtonInstance>(null);

  const [containerWidth, setContainerWidth] = useState<number>();
  const [primaryHovered, setPrimaryHovered] = useState(false);
  const [secondaryHovered, setSecondaryHovered] = useState(false);

  const containerStyles = useMemo(
    () =>
      fluid
        ? { minWidth: toPixels(containerWidth) }
        : {
            width: toPixels(containerWidth),
            paddingRight: toPixels(CONTAINER_PADDING_RIGHT),
          },
    [fluid, containerWidth]
  );
  const [primaryDisabled, secondaryDisabled] = useMemo(() => (Array.isArray(disabled) ? disabled : [disabled, disabled]), [disabled]);
  const [primaryCompleted, secondaryCompleted] = useMemo(() => (Array.isArray(completed) ? completed : [completed, completed]), [completed]);

  useEffect(function calcContainerWidth() {
    const containerWidth = Math.max(depositButtonRef.current?.getWidth() ?? 0, withdrawButtonRef.current?.getWidth() ?? 0, removeButtonRef.current?.getWidth() ?? 0);
    setContainerWidth(containerWidth + CONTAINER_PADDING_RIGHT);
  }, []);

  const createMouseEnterHandler = (buttonType: "primary" | "secondary") => () => {
    if (buttonType === "primary" && !primaryDisabled) {
      setPrimaryHovered(true);
      return;
    }

    if (buttonType === "secondary" && !secondaryDisabled) {
      setSecondaryHovered(true);
      return;
    }
  };

  const createMouseLeaveHandler = (buttonType: "primary" | "secondary") => () => {
    if (buttonType === "primary" && !primaryDisabled) {
      setPrimaryHovered(false);
      return;
    }

    if (buttonType === "secondary" && !secondaryDisabled) {
      setSecondaryHovered(false);
      return;
    }
  };

  return (
    <div className={twJoin("deposit-button relative h-10 overflow-hidden border-neutral-600", fluid && "w-full")} style={containerStyles}>
      <div className="absolute h-20 left-0 right-10 top-0">
        <div
          className={twJoin(
            "absolute flex flex-col left-0 top-0 w-full transition-transform duration-300",
            primaryCompleted && !primaryDisabled ? "-translate-y-10" : "translate-y-0"
          )}
          onMouseEnter={createMouseEnterHandler("primary")}
          onMouseLeave={createMouseLeaveHandler("primary")}
        >
          <BaseButton
            ref={depositButtonRef}
            disabled={primaryDisabled}
            textCenter={fluid && ((!secondaryHovered && defaultButton === "primary") || (primaryHovered && defaultButton === "secondary"))}
            className="w-full"
            icon={<SupDepositAlt width={24} height={24} />}
            onClick={onDeposit}
          >
            Deposit
          </BaseButton>
          <RemoveButton
            hovered={primaryHovered}
            ref={removeButtonRef}
            delayAnimation={defaultButton === "secondary"}
            textCenter={(!secondaryHovered && defaultButton === "primary") || (primaryHovered && defaultButton === "secondary")}
            className="w-full"
            onClick={onRemoveDeposit}
          />

          {primaryDisabled && <div className="absolute inset-0" />}
        </div>
        <div
          className={twJoin(
            "absolute flex flex-col left-full top-0 w-full transition-transform duration-300",
            secondaryCompleted && !secondaryDisabled ? "-translate-y-10" : "translate-y-0",
            ((defaultButton === "primary" && !secondaryHovered) || (defaultButton === "secondary" && primaryHovered)) && "translate-x-0",
            ((defaultButton === "primary" && secondaryHovered) || (defaultButton === "secondary" && !primaryHovered)) && "-translate-x-[calc(100%-40px)]"
          )}
          onMouseEnter={createMouseEnterHandler("secondary")}
          onMouseLeave={createMouseLeaveHandler("secondary")}
        >
          <BaseButton
            secondary
            ref={withdrawButtonRef}
            disabled={secondaryDisabled}
            textCenter={fluid && ((secondaryHovered && defaultButton === "primary") || (!primaryHovered && defaultButton === "secondary"))}
            className="w-full"
            icon={<SupWithdrawAlt width={24} height={24} />}
            onClick={onWithdraw}
          >
            Withdraw
          </BaseButton>
          <RemoveButton
            hovered={secondaryHovered}
            delayAnimation={defaultButton === "primary"}
            textCenter={(secondaryHovered && defaultButton === "primary") || (!primaryHovered && defaultButton === "secondary")}
            className="w-full"
            onClick={onRemoveWithdraw}
          />

          {secondaryDisabled && <div className="absolute inset-0" />}
        </div>
      </div>

      {!secondaryCompleted && (
        <span
          className={twJoin(
            "absolute block h-10 border-r right-0 top-0 transition-colors duration-300",
            !secondaryHovered && !secondaryDisabled && "border-neutral-600",
            secondaryHovered && !secondaryDisabled && "border-neutral-700",
            secondaryDisabled && defaultButton === "primary" && "border-neutral-100"
          )}
        />
      )}
    </div>
  );
}
